import BaseComponent from './BaseComponent';

const POSTER_DOWNLOAD_FILENAME = 'poster-word-docent.jpg';
const POSTER_CELL_WIDTH = 1240;
const POSTER_CELL_HEIGHT = 1750;

const POSTER_LANDSCAPE_LEFT_WIDTH = 3094;
const POSTER_LANDSCAPE_RIGHT_WIDTH = 4641;
const POSTER_LANDSCAPE_CELL_HEIGHT = 2188;

const POSTER_TRIANGLE_HEIGHT_RATIO = 0.16;
const POSTER_LANDSCAPE_TRIANGLE_WIDTH_RATIO = 0.1;

const POSTER_EXPORT_QUALITY = 0.92;
const POSTER_LANDSCAPE_SUFFIX = '-horizontal';

export default class PosterGenerator extends BaseComponent {
    constructor(element) {
        super();

        this.element = element;
        this.posterCells = {
            'top-left': this.element.querySelector('[data-poster-cell="top-left"]'),
            'top-right': this.element.querySelector('[data-poster-cell="top-right"]'),
            'bottom-left': this.element.querySelector('[data-poster-cell="bottom-left"]'),
            'bottom-right': this.element.querySelector('[data-poster-cell="bottom-right"]'),
        };
        this.downloadButton = this.element.querySelector('[data-poster-download]');
        this.posterGrid = this.element.querySelector('[data-poster-grid]');
        this.templateButtons = Array.from(this.element.querySelectorAll('[data-poster-template-option]'));
        this.orientationButtons = Array.from(this.element.querySelectorAll('[data-poster-orientation]'));
        this.uploadLabelsBySlot = new Map(
            Array.from(this.element.querySelectorAll('[data-poster-input]'))
                .filter((input) => input.dataset.posterInput)
                .map((input) => [input.dataset.posterInput, input.closest('.t-poster-generator__controls-button--upload')])
        );
        this.uploadedObjectUrls = new Map();
        this.selectedTemplateButton = null;

        this.state = {
            'top-left': null,
            'top-right': null,
            'bottom-left': null,
            'bottom-right': null,
        };
        this.triangleColor = '#ffffff';
        this.orientation = 'portrait';

        this.addListener(this.element, 'change', this.handleInputChange);
        this.addListener(this.element, 'click', this.handleClick);

        const initialTemplateButton =
            this.templateButtons.find((button) => button.getAttribute('aria-pressed') === 'true') ??
            this.templateButtons[0];

        if (initialTemplateButton) {
            this.selectTemplate(initialTemplateButton);
        }

        const initialOrientationButton =
            this.orientationButtons.find((button) => button.getAttribute('aria-pressed') === 'true') ??
            this.orientationButtons[0];

        if (initialOrientationButton) {
            this.selectOrientation(initialOrientationButton);
        }

        this.updateDownloadState();
    }

    handleInputChange(event) {
        const target = event.target;

        if (!(target instanceof HTMLInputElement) || target.type !== 'file') {
            return;
        }

        const slot = target.dataset.posterInput;
        const file = target.files?.[0];

        if (!slot) {
            return;
        }

        if (!file) {
            this.clearUploadedImage(slot);
            this.setUploadActiveState(slot, false);
            this.updateDownloadState();
            return;
        }

        this.updateUploadedImage(slot, file);
        this.setUploadActiveState(slot, true);
    }

    handleClick(event) {
        const target = event.target;

        if (!(target instanceof HTMLElement)) {
            return;
        }

        const templateButton = target.closest('[data-poster-template-option]');
        if (templateButton instanceof HTMLButtonElement) {
            this.selectTemplate(templateButton);
            return;
        }

        const orientationButton = target.closest('[data-poster-orientation]');
        if (orientationButton instanceof HTMLButtonElement) {
            this.selectOrientation(orientationButton);
            return;
        }

        const downloadButton = target.closest('[data-poster-download]');
        if (downloadButton instanceof HTMLButtonElement) {
            event.preventDefault();
            this.downloadPoster();
        }
    }

    updateUploadedImage(slot, file) {
        const previousObjectUrl = this.uploadedObjectUrls.get(slot);

        if (previousObjectUrl) {
            URL.revokeObjectURL(previousObjectUrl);
        }

        const objectUrl = URL.createObjectURL(file);

        this.uploadedObjectUrls.set(slot, objectUrl);
        this.state[slot] = objectUrl;
        this.renderCell(slot, objectUrl);
        this.updateDownloadState();
    }

    clearUploadedImage(slot) {
        const previousObjectUrl = this.uploadedObjectUrls.get(slot);

        if (previousObjectUrl) {
            URL.revokeObjectURL(previousObjectUrl);
            this.uploadedObjectUrls.delete(slot);
        }

        this.state[slot] = null;

        const cell = this.posterCells[slot];
        if (cell) {
            cell.style.backgroundImage = '';
            cell.classList.remove('has-image');
        }
    }

    setUploadActiveState(slot, isActive) {
        const uploadLabel = this.uploadLabelsBySlot.get(slot);

        if (!uploadLabel) {
            return;
        }

        uploadLabel.classList.toggle('is-active', isActive);
    }

    selectTemplate(selectedButton) {
        this.selectedTemplateButton = selectedButton;
        this.templateButtons.forEach((button) => {
            const isActive = button === selectedButton;

            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        const templateSrc = this.getTemplateSource(selectedButton);

        if (!templateSrc) {
            return;
        }

        this.state['bottom-left'] = templateSrc;
        this.renderCell('bottom-left', templateSrc);
        this.setTriangleColor(selectedButton.dataset.triangleColor);
        this.updateDownloadState();
    }

    selectOrientation(selectedButton) {
        const orientation = selectedButton.dataset.orientation;

        if (!orientation) {
            return;
        }

        this.orientationButtons.forEach((button) => {
            const isActive = button === selectedButton;

            button.classList.toggle('is-active', isActive);
            button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        this.orientation = orientation;

        if (this.posterGrid) {
            this.posterGrid.classList.toggle('is-landscape', orientation === 'landscape');
        }

        if (this.selectedTemplateButton) {
            const templateSrc = this.getTemplateSource(this.selectedTemplateButton);

            if (templateSrc) {
                this.state['bottom-left'] = templateSrc;
                this.renderCell('bottom-left', templateSrc);
                this.updateDownloadState();
            }
        }
    }

    getTemplateSource(button) {
        const baseSrc = button.dataset.src;

        if (!baseSrc) {
            return null;
        }

        if (this.orientation !== 'landscape') {
            return baseSrc;
        }

        if (baseSrc.includes(POSTER_LANDSCAPE_SUFFIX)) {
            return baseSrc;
        }

        return baseSrc.replace(/\.jpg$/i, `${POSTER_LANDSCAPE_SUFFIX}.jpg`);
    }

    setTriangleColor(colorToken) {
        if (!colorToken) {
            return;
        }

        if (this.posterGrid) {
            this.posterGrid.style.setProperty('--poster-triangle-color', `var(${colorToken})`);
        }

        const resolvedColor = getComputedStyle(document.documentElement).getPropertyValue(colorToken).trim();

        if (resolvedColor) {
            this.triangleColor = resolvedColor;
        }
    }

    renderCell(slot, imageSrc) {
        const cell = this.posterCells[slot];

        if (!cell) {
            return;
        }

        cell.style.backgroundImage = `url("${imageSrc}")`;
        cell.classList.add('has-image');
    }

    updateDownloadState() {
        if (!this.downloadButton) {
            return;
        }

        const hasAllImages = Object.values(this.state).every(Boolean);
        this.downloadButton.disabled = !hasAllImages;
    }

    async downloadPoster() {
        if (!this.downloadButton || this.downloadButton.disabled) {
            return;
        }

        this.downloadButton.disabled = true;

        try {
            const blob = await this.buildPosterBlob();

            if (window.showSaveFilePicker) {
                await this.saveWithFilePicker(blob);
            } else {
                this.saveWithDownloadLink(blob);
            }
        } catch (error) {
            console.error('Poster kon niet worden gegenereerd.', error);
        } finally {
            this.updateDownloadState();
        }
    }

    async buildPosterBlob() {
        const canvas = await this.renderPosterToCanvas();

        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    if (!blob) {
                        reject(new Error('Poster blob unavailable'));
                        return;
                    }
                    resolve(blob);
                },
                'image/jpeg',
                POSTER_EXPORT_QUALITY
            );
        });
    }

    async renderPosterToCanvas() {
        const canvas = document.createElement('canvas');
        const layout = this.getLayoutMetrics();
        const canvasWidth = layout.canvasWidth;
        const canvasHeight = layout.canvasHeight;
        const context = canvas.getContext('2d');

        if (!context) {
            throw new Error('Canvas context unavailable');
        }

        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        context.fillStyle = '#ffffff';
        context.fillRect(0, 0, canvasWidth, canvasHeight);

        const cells = [
            { slot: 'top-left', x: 0, y: 0, width: layout.leftWidth },
            { slot: 'top-right', x: layout.leftWidth, y: 0, width: layout.rightWidth },
            { slot: 'bottom-left', x: 0, y: layout.cellHeight, width: layout.leftWidth },
            { slot: 'bottom-right', x: layout.leftWidth, y: layout.cellHeight, width: layout.rightWidth },
        ];

        for (const cell of cells) {
            const imageSource = this.state[cell.slot];

            if (!imageSource) {
                throw new Error(`Missing image for ${cell.slot}`);
            }

            const image = await this.loadImage(imageSource);

            this.drawImageCover(context, image, cell.x, cell.y, cell.width, layout.cellHeight);
        }

        this.drawTriangleOverlay(context, layout);

        return canvas;
    }

    async saveWithFilePicker(blob) {
        const pickerOptions = {
            suggestedName: POSTER_DOWNLOAD_FILENAME,
            types: [
                {
                    description: 'JPEG Image',
                    accept: {
                        'image/jpeg': ['.jpg', '.jpeg'],
                    },
                },
            ],
        };

        const handle = await window.showSaveFilePicker(pickerOptions);
        const writable = await handle.createWritable();

        await writable.write(blob);
        await writable.close();
    }

    saveWithDownloadLink(blob) {
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = objectUrl;
        link.download = POSTER_DOWNLOAD_FILENAME;

        document.body.append(link);
        link.click();
        link.remove();

        URL.revokeObjectURL(objectUrl);
    }

    getLayoutMetrics() {
        if (this.orientation === 'landscape') {
            const leftWidth = POSTER_LANDSCAPE_LEFT_WIDTH;
            const rightWidth = POSTER_LANDSCAPE_RIGHT_WIDTH;
            const cellHeight = POSTER_LANDSCAPE_CELL_HEIGHT;

            return {
                leftWidth,
                rightWidth,
                cellHeight,
                canvasWidth: leftWidth + rightWidth,
                canvasHeight: cellHeight * 2,
            };
        }

        return {
            leftWidth: POSTER_CELL_WIDTH,
            rightWidth: POSTER_CELL_WIDTH,
            cellHeight: POSTER_CELL_HEIGHT,
            canvasWidth: POSTER_CELL_WIDTH * 2,
            canvasHeight: POSTER_CELL_HEIGHT * 2,
        };
    }

    drawTriangleOverlay(context, layout) {
        if (this.orientation === 'landscape') {
            const triangleWidth = layout.rightWidth * POSTER_LANDSCAPE_TRIANGLE_WIDTH_RATIO;
            const x = layout.leftWidth;
            const y = layout.cellHeight;

            context.save();
            context.fillStyle = this.triangleColor;
            context.beginPath();
            context.moveTo(x, y);
            context.lineTo(x + triangleWidth, y + layout.cellHeight / 2);
            context.lineTo(x, y + layout.cellHeight);
            context.closePath();
            context.fill();
            context.restore();
            return;
        }

        const triangleHeight = layout.cellHeight * POSTER_TRIANGLE_HEIGHT_RATIO;

        context.save();
        context.fillStyle = this.triangleColor;
        context.beginPath();
        context.moveTo(0, layout.cellHeight);
        context.lineTo(layout.leftWidth / 2, layout.cellHeight - triangleHeight);
        context.lineTo(layout.leftWidth, layout.cellHeight);
        context.closePath();
        context.fill();
        context.restore();
    }

    loadImage(source) {
        return new Promise((resolve, reject) => {
            const image = new Image();

            image.decoding = 'async';
            image.onload = () => resolve(image);
            image.onerror = () => reject(new Error(`Could not load image: ${source}`));
            image.src = source;
        });
    }

    drawImageCover(context, image, x, y, width, height) {
        const imageRatio = image.width / image.height;
        const destinationRatio = width / height;

        let sourceWidth = image.width;
        let sourceHeight = image.height;
        let sourceX = 0;
        let sourceY = 0;

        if (imageRatio > destinationRatio) {
            sourceWidth = image.height * destinationRatio;
            sourceX = (image.width - sourceWidth) / 2;
        } else {
            sourceHeight = image.width / destinationRatio;
            sourceY = (image.height - sourceHeight) / 2;
        }

        context.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, x, y, width, height);
    }

    destroy() {
        this.uploadedObjectUrls.forEach((objectUrl) => {
            URL.revokeObjectURL(objectUrl);
        });
        this.uploadedObjectUrls.clear();

        super.destroy();
    }
}
